import { RefreshTokenResponse } from '../types';
import { nowInUnixEpochTime } from 'utils';
import jwtDecode from 'jwt-decode';
import FetchWrapper from './FetchWrapper';

const TOKEN = 'TOKEN';
const REFRESH_TOKEN = 'REFRESH_TOKEN';

class TokenManager {
    timeoutId: number | null = null;
    accessExpirationHandler: (args?: any) => any = () => {};

    clearTokens = (): void => {
        this.setAccessToken(null);
        this.setRefreshToken(null);
    };

    setAccessToken = (token: string | null): void => {
        return localStorage.setItem(TOKEN, token || '');
    };

    setRefreshToken = (token: string | null): void => {
        return localStorage.setItem(REFRESH_TOKEN, token || '');
    };

    getRefreshToken = (): string | null => {
        return localStorage.getItem(REFRESH_TOKEN);
    };

    getAccessToken = async (): Promise<string | null> => {
        const accessToken: string | null = localStorage.getItem(TOKEN);
        if (!accessToken) return null;
        if (!this.tokenIsValid(accessToken) && !!this.getRefreshToken()) {
            await this.reauthenticate();
        }
        return localStorage.getItem(TOKEN);
    };

    tokenIsValid = (token: string): boolean => {
        try {
            return nowInUnixEpochTime() < jwtDecode<{ exp: number }>(token).exp;
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    getTimeToAccessExpiration = async (): Promise<false | number> => {
        const token = await this.getAccessToken();
        if (!token) return false;
        const decoded = jwtDecode<{ exp: number }>(token);
        return decoded.exp - nowInUnixEpochTime();
    };

    setAccessTokenExpirationHandler = async (func: any): Promise<boolean> => {
        // It's possible that we don't have a refresh token when this gets called
        // so we return true/false indicating successful handler setting.
        try {
            if (this.timeoutId) {
                clearTimeout(this.timeoutId);
            }
            const time: number | false = await this.getTimeToAccessExpiration();
            if (!time) {
                return false;
            }
            const timeInMilliseconds: number = time * 1000;
            this.accessExpirationHandler = async (): Promise<void> => {
                await this.reauthenticate();
                if (!this.getAccessToken()) {
                    func();
                }
            };
            const timeoutValue: any = setTimeout(this.accessExpirationHandler, timeInMilliseconds);
            this.timeoutId = timeoutValue;

            return true;
        } catch (e) {
            console.error(e);
            return false;
        }
    };

    reauthenticate = async (): Promise<void> => {
        try {
            const res = await FetchWrapper({
                path: 'auth/refresh',
                mode: 'cors',
                method: 'POST',
                body: {
                    refreshToken: this.getRefreshToken(),
                },
            });
            const { jwt }: RefreshTokenResponse = await res.json();
            if (!jwt) {
                throw new Error('Something went wrong when re-authenticating user');
            }
            return this.setAccessToken(jwt.authToken);
        } catch (error) {
            // User session is timed out or internal server error. In either
            // case the app is not available to the user
            console.error(error);
            this.clearTokens();
        }
    };
}

export default new TokenManager();
